import { Component, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { signal } from '@angular/core';
import {
  CapitalizeWithSpacesPipe,
  IListing,
  ListingsService,
} from '@out-web/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { useImages } from '@out-web/core';
import { GalleryComponent } from '../gallery/gallery.component';
import { ThumbnailsComponent } from '../thumbnail/thumbnail.component';
import {
  CarouselLoaderComponent,
  ListingContactSellerComponent,
  SimilarListingComponent,
} from '@out-web/components';

@Component({
  selector: 'out-listing-detail-v2',
  imports: [
    CommonModule,
    GalleryComponent,
    ThumbnailsComponent,
    ListingContactSellerComponent,
    SimilarListingComponent,
    RouterLink,
    CapitalizeWithSpacesPipe,
    CarouselLoaderComponent
  ],
  templateUrl: './listing-detail-v2.component.html',
  styleUrl: './listing-detail-v2.component.scss',
})
export class ListingDetailV2Component implements OnInit {
  imageIndex = signal(0);
  isGalleryOpen = signal(false);
  showEnquiryModal = signal(false);
  showTaxReliefInfoModal = signal(false);
  isSendingEnquiry = signal(false);
  isSkeletonLoad = signal(true);
  hasEnquired = signal(false);
  loggedIn = signal(false);
  enquiry = signal('');

  attr: any = {
    title: '',
    saleType: '',
    condition: '',
    price: '',
    taxReliefPrice: '',
    miles: '',
    images: { gallery: [] },
  };

  listingId: string | null = null;
  listingData: IListing | null = null;
  loading = false;
  videoUrl = '';
  videoThumbnailUrl = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private listingService: ListingsService,
    private titleService: Title,
    private meta: Meta,
  ) { }

  ngOnInit(): void {
    this.setRouteParams();
    this.setTitleAndMeta();
  }

  private setRouteParams() {
    this.route.paramMap.subscribe((params) => {
      this.listingId = params.get('id');
      if (this.listingId) {
        this.fetchListingData(this.listingId);
      }
      else {
        this.router.navigate(['/used-tesla-for-sale']);
      }
    });
  }

  fetchListingData(id: string): void {
    this.loading = true;
    this.listingService.fetchListing(id).subscribe({
      next: (data) => {

        if (!data || !data.content) {
          console.error('No listing data found, redirecting...');
          this.router.navigate(['/used-tesla-for-sale']);
          this.loading = false;
          return;
        }

        this.listingData = data;
        this.attr.title = data.content.title;
        this.attr.saleType = data.type;
        this.attr.condition = data.vehicle.condition;
        this.attr.price = data.price.value;
        this.attr.taxReliefPrice = data.price.value - 4000;
        this.attr.miles = data.vehicle.mileage;
        this.attr.images = useImages(data?.content?.images || []).images;
        // Process video URL if available
        if (data.content.video && data.content.video.url) {
          this.videoUrl = data.content.video.url;
          this.getVimeoThumbnail(this.videoUrl);
        }
        this.loading = false;
        this.setTitleAndMeta();
      },
      error: (error) => {
        console.error('Error fetching listing:', error);
        this.router.navigate(['/used-tesla-for-sale']);
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  getVimeoThumbnail(vimeoUrl: string): void {
    const match = vimeoUrl.match(/(?:vimeo\.com\/(?:video\/)?)(\d+)/);
    const vimeoId = match ? match[1] : null;
    if (!vimeoId) {
      console.error('Invalid Vimeo URL');
      return;
    }
    fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vimeoId}`)
      .then((response) => response.json())
      .then((data) => {
        let thumb = data.thumbnail_url;
        // Change resolution if desired
        thumb = thumb.replace(/_[\d]+x[\d]+/, '_1280x720');
        this.videoThumbnailUrl = thumb;
        console.log('Vimeo Thumbnail URL:', this.videoThumbnailUrl);

        // (Optional) If you want to inject the thumbnail into your gallery:
        if (this.attr.images && Array.isArray(this.attr.images.gallery)) {
          // Insert thumbnail at a specific position if not already there
          if (!this.attr.images.gallery.includes(this.videoThumbnailUrl)) {
            this.attr.images.gallery.splice(1, 0, this.videoThumbnailUrl);
          }
        }
      })
      .catch((error) =>
        console.error('Error fetching Vimeo thumbnail:', error)
      );
  }

  openGallery(idx?: number): void {
    if (idx !== undefined) {
      this.imageIndex.set(idx);
    }
    this.isGalleryOpen.set(true);
  }

  closeGallery(): void {
    this.isGalleryOpen.set(false);
    this.imageIndex.set(0);
  }

  getFormattedDescription(): string {
    const description = this.listingData?.content?.description;
    return description ? description.replace(/\n<p>/g, '<br /><p>') : '';
  }

  setTitleAndMeta() {

    this.titleService.setTitle(
      (this.listingData?.vehicle?.year || '') +
      ' • ' +
      (this.listingData?.vehicle?.model || '') +
      ' • ' +
      (this.listingData?.vehicle?.trim || '') +
      ' • ' +
      (this.listingData?.vehicle?.specification?.colour || '') + '| Sell Your Tesla | Only Used Tesla'
    );

    this.meta.addTags([
      {
        name: 'description',
        property: 'description',
        content:
          'Only Used Tesla | A Place Exclusively Dedicated To Buying And Selling Used Teslas',
        vmid: 'description',
      },
      {
        name: 'og:description',
        property: 'og:description',
        content:
          'Only Used Tesla | A Place Exclusively Dedicated To Buying And Selling Used Teslas',
        hid: 'og:description',
      },
      {
        name: 'keywords',
        content:
          'Used Tesla, Buy Tesla USA, Tesla Model 3, Tesla Model S, Used Electric Cars, Affordable Tesla, Certified Tesla',
      },
      { name: 'author', content: 'Only Used Tesla' },
    ]);
  }

}
